import { Api } from "@mobibamjs/api";
import { IMember } from "@mobibamjs/interface";
import { MobibamEnv } from "./engine/Common";
import { IProject } from "./engine/Project";

export interface Mobibam {
    readonly MOB_ENV: MobibamEnv;
    readonly MOB_CONFIGURATOR_BASEURI: string;
    readonly MOB_SHOP_BASEURI: string;
    readonly MOB_PRODUCT_BASEURI: string;
    readonly MOB_CDN_BASEURI: string;
    readonly MOB_RECAPTCHA_SITEKEY: string;
    readonly MOB_STRIPE_PUBKEY: string;
    readonly MOB_BRAND_KEY: string;
}

export type Modals = "Login" | "SignUp" | "ForgotPass" | "Save" | "Desktop" | "NONE";

export interface SaveProjectParam {

    /**
     * The project to save (see IEngine.project)
     */
    project: IProject;

    /**
     * The owner id of the project
     */
    owner_id: number;

    /**
     * The operator id of the project.
     * If not specified, same as owner_id.
     */
    operator_id?: number;

    /**
     * The mode of saving. Default is "save"
     */
    mode?: "save" | "saveAs";

    /**
     * The title of project. Default is an auto-generated title based on the model name.
     */
    title?: string;
}

export interface IStore {

    /**
     * The Mobibam settings as is injected in page by WordPress Mobibam Plugin
     */
    readonly mobibam: Mobibam;

    /**
     * The id of project to open specified in query-string as ?ProjectId=<id-of-project>
     */
    readonly openedProjectId: number;

    /**
     * The current name of theme used by the configurator app (ex. 'LM', 'camif', etc ...)
     */
    readonly themeName: string;

    /**
     * The available depths of closet
     */
    readonly easyClosetDepths: ReadonlyArray<number>;

    /**
     * Save or Save As the specified project. Project image will be saved, and owner_id will be synchronized.
     */
    saveProject(p: SaveProjectParam): Promise<void>;

    /**
     * Open or close modal dialog
     */
    currentModal: Modals;

    /**
     * Display a notification at the bottom of 3D area.
     * @param msg The message of notification
     */
    showNotif(msg: string): void;

    /**
     * Gets the current displayed notification
     */
    readonly currentNotif: string | null;

    /**
     * The current logged-in user
     */
    currentUser: IMember | null;
}

export class Store implements IStore {

    private _timeoutID: number = -1;

    private _currentUser: IMember | null = null;

    constructor() {
        this.mobibam = (window as any).Mobibam as Mobibam;
        if (this.mobibam === undefined) throw new Error(`window.Mobibam variable is missing in current document`);
        if (this.mobibam.MOB_CONFIGURATOR_BASEURI === undefined) throw new Error(`window.Mobibam.MOB_CONFIGURATOR_BASEURI is missing`);
        if (this.mobibam.MOB_BRAND_KEY === undefined) throw new Error(`window.Mobibam.MOB_BRAND_KEY is missing`);

        // Read cookie theme name
        const cookieKvp = document.cookie.split("; ").find((row) => row.toLowerCase().startsWith("theme="));
        this.themeName = cookieKvp ? cookieKvp.split("=")[1] : "";

        // Parse query string
        const query = window.location.search.substring(1);
        const params = query.split("&");
        for(const param of params) {
            const kvp = param.split("=");
            switch(decodeURIComponent(kvp[0])) {

                case "ProjectId":
                    // console.debug("ProjectId => %d", parseInt(decodeURIComponent(kvp[1])));
                    this.openedProjectId = parseInt(decodeURIComponent(kvp[1]));
                    break;

                // Override cookie theme
                case "Theme":
                case "theme":
                    this.themeName = decodeURIComponent(kvp[1]);
                    break;
            }
        }

        console.log("[MOB] Current theme is => %s", this.themeName ? this.themeName : "(default)");
    }

    readonly mobibam: Mobibam;

    readonly openedProjectId: number = 0;

    readonly themeName: string = "";

    readonly easyClosetDepths: ReadonlyArray<number> = [25,33,40];

    async saveProject(p: SaveProjectParam): Promise<void> {
        const mode = p.mode ? p.mode : "save";
        if (mode === "save") {
            await p.project.save(p.owner_id, p.operator_id ? p.operator_id : p.owner_id, p.title);
            console.debug(`[MOB] Project #${p.project.id} was 'Saved' successfully with title '${p.project.title}'`);
        }
        else {
            await p.project.saveAs(p.owner_id, p.operator_id ? p.operator_id : p.owner_id, p.title);
            console.debug(`[MOB] Project #${p.project.id} was 'Saved As' successfully with title '${p.project.title}'`);
        }

        await p.project.saveImage();
        
        Api.sync.addMember(p.owner_id, true, ["sendinblue"]).then(() => {
            console.debug(`[MOB] Sync update of member #${p.owner_id} in sendinblue succeeded`)
        }).catch((err: Error) => {
            console.error(`[MOB] Sync update of member #${p.owner_id} in sendinblue failed with error => ${err.message}`)
        })

        this.showNotif("Votre projet à bien été enregistré !");
    }

    private _currentModal: Modals = "NONE";

    get currentModal(): Modals {
        return this._currentModal
    }

    set currentModal(value: Modals) {
        this._currentModal = value;
    }

    showNotif(msg: string): void {
        if (this._timeoutID > -1) window.clearTimeout(this._timeoutID);

        this.currentNotif = msg;
        this._timeoutID = window.setTimeout(() => { 
            this.currentNotif = null ;
            this._timeoutID = -1;
        }, 5000);
    }

    currentNotif: string | null = null;

    get currentUser(): IMember | null { return this._currentUser }
    set currentUser(user: IMember | null) {
        this._currentUser = user;
        console.log("[MOB] Current logged in user is => %s", user ? `#${user.id} - ${user.email}` : "(none)");
    }
}